import { ClientRepo } from "@services/clients/client.repo";
import { AuthService } from "@common/auth.service";
import angular = require("angular");
import { ClientDetails, IndividualClientDetails, InstitutionClientDetails } from "@models/clients/clientDetailsModel";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import { UserItem } from "@models/clients/userItem";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { extractErrorMessage } from "@common/utils/errorUtils";


class ClientDetailsController implements angular.IOnInit {
  public isLoading: boolean = false;
  public client: ClientDetails;
  public title: string;
  public clientNumber: string; // Add clientNumber property for child components
  
  // User management properties
  public users: UserItem[] = [];
  public selectedAdmin: UserItem | null = null;
  public selectedRelationshipManager: UserItem | null = null;
  public selectedInvestmentOfficer: UserItem | null = null;
  public isLoadingUsers: boolean = true;
  public displayName: string;
  
  // Save state management
  public isSaving: boolean = false;
  public lastSaved: Date | null = null;
  
  // Dropdown options management
  public dropdownOptions: any = {};
  public isLoadingDropdownOptions: boolean = false;

  /** Index of selected tab (0=General, 1=Profile, 2=Account Reference, 3=Portfolio, etc.). Used for deep-linking via ?tab=portfolio */
  public selectedTabIndex: number = 0;

  /** @ngInject */
  constructor(
    private clientRepo: ClientRepo,
    private clientDetailsService: ClientDetailsService,
    private clientCRMRepo: ClientCRMRepo,
    private dropdownOptionsService: DropdownOptionsService,
    private $scope: angular.IScope,
    private $rootScope: angular.IRootScopeService,
    private $state: angular.ui.IStateService,
    private $stateParams: angular.ui.IStateParamsService,
    private $mdDialog: angular.material.IDialogService,
    private $q: angular.IQService,
    private notifyjs: any,
    private authService: AuthService
  ) {}

  public loading: boolean = false;

  /** Tab names mapped to index for deep-linking */
  private static readonly TAB_INDEX: Record<string, number> = {
    general: 0,
    profile: 1,
    accountReference: 2,
    portfolio: 3,
    reporting: 4,
    bankingActivities: 5,
    recipients: 6
  };

  $onInit() {
    let clientNumber = this.$stateParams["id"];
    this.clientNumber = clientNumber; // Store clientNumber for child components
    console.log("clientNumber", this.clientNumber);

    const tabParam = (this.$stateParams["tab"] || "").toLowerCase();
    if (tabParam && ClientDetailsController.TAB_INDEX.hasOwnProperty(tabParam)) {
      this.selectedTabIndex = ClientDetailsController.TAB_INDEX[tabParam];
    }
    
    // Load dropdown options first (this will use cache if available)
    this.loadDropdownOptions();
        
    this.loading = true;
    this.isLoadingUsers = true;
    // For existing clients, load both client and users concurrently
    const clientPromise = this.clientRepo.getClient(clientNumber);
    const usersPromise = this.clientCRMRepo.getUsers();
    
    this.$q.all([clientPromise, usersPromise])
      .then(([clientResult, usersResult]) => {
        // Set client data
        this.client = clientResult.data as ClientDetails;
        
        // Set users data
        this.users = usersResult.data as UserItem[];
        
        // Set selected users based on client data
        if (this.client.administeredBy) {
          this.selectedAdmin = this.findUserById(this.client.administeredBy);
        }
        if (this.client.relationshipManager) {
          this.selectedRelationshipManager = this.findUserById(this.client.relationshipManager);
        }
        if (this.client.managedBy) {
          this.selectedInvestmentOfficer = this.findUserById(this.client.managedBy);
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to load client data: " + extractErrorMessage(err), "error");
      })
      .finally(() => {
        this.loading = false;
        this.isLoadingUsers = false;
      });
  }

  // Shared save functionality for all tabs
  public saveClientInfo(): void {
    if (!this.client) {
      this.notifyjs("No client data to save", "error");
      return;
    }

    // Validate required fields before saving
    if (!this.validateRequiredFields()) {
      return;
    }

    this.isSaving = true;
    
    // Ensure user management fields are properly assigned
    this.assignUserManagementFields();
    
    this.clientCRMRepo.saveClient(this.client)
        .then((result: any) => {
            this.lastSaved = new Date();
            this.notifyjs("Client information saved successfully", "success");
            
            // Check if this was a new client (id was 0) and we got a new client number back
            if (this.client.id === 0 && result.data && result.data.clientId) {
                // Update the client object with the new ID
                this.client.id = result.data.clientId;
                
                // Use the client number from the response for navigation
                const clientNumber = result.data.clientNumber || this.client.clientNumber;
                
                // Redirect to the correct URL with the new client number
                this.$state.go('root.portfolioManagement.clients.detail', { 
                    id: clientNumber 
                }, { 
                    reload: true 
                });
            }
        })
        .catch((err) => {
            this.notifyjs("Failed to save client information: " + extractErrorMessage(err), "error");
        })
        .finally(() => {
            this.isSaving = false;
        });
  }

  private validateRequiredFields(): boolean {
    const errors: string[] = [];

    // Client number is always required
    if (!this.client.clientNumber || String(this.client.clientNumber) == '') {
      errors.push('Client Number is required');
    } else if (String(this.client.clientNumber).startsWith('0')) {
      errors.push('Client number cannot start with 0');
    }

    // Check client type specific requirements
    if (this.isIndividualClient()) {
      const individualClient = this.client as IndividualClientDetails;
      if (!individualClient.firstName || individualClient.firstName.trim() === '') {
        errors.push('First Name is required for individual clients');
      }
      if (!individualClient.lastName || individualClient.lastName.trim() === '') {
        errors.push('Last Name is required for individual clients');
      }
    } else if (this.isInstitutionClient()) {
      if (!this.client.name || this.client.name.trim() === '') {
        errors.push('Company Name is required for institution clients');
      }
    }

    // Strategy text validation — strip HTML before checking so an empty Quill editor doesn't pass
    const stripHtml = (html: string): string =>
      html ? html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '';
    
    // Strategy image validation - Images can also be placed in the strategy section 
    const imageRegex = /<img\s[^>]*>/i;

    if (this.client.isLocalStrategyEnabled && !stripHtml(this.client.localStrategy) && !imageRegex.test(this.client.localStrategy)) {
      errors.push('Local Strategy text is required when Local is selected');
    }
    if (this.client.isInternationalStrategyEnabled && !stripHtml(this.client.internationalStrategy) && !imageRegex.test(this.client.internationalStrategy)) {
      errors.push('International Strategy text is required when International is selected');
    }

    // Show validation errors if any
    if (errors.length > 0) {
      this.notifyjs("Please fill in all required fields: " + errors.join(', '), "error");
      return false;
    }

    return true;
  }


  public findUserById(id: number): UserItem {
    return this.users.find(user => user.id === id);
  }

  public filterUsers(searchText: string): UserItem[] {
    if (!searchText || searchText.trim() === '') {
        return this.users;
    }
    
    const query = searchText.toLowerCase().trim();
    return this.users.filter(user => 
        user.name && user.name.toLowerCase().includes(query)
    );
  }

  public updateAdministeredBy(user: UserItem): void {
    if(this.client) {
        if (user) {
            this.client.administeredBy = user.id;
            this.selectedAdmin = user; // Update the parent's selected user
        } else {
            this.client.administeredBy = null; // Clear the user
            this.selectedAdmin = null;
        }
    }
  }

  public updateRelationshipManager(user: UserItem): void {
    if(this.client) {
        if (user) {
            this.client.relationshipManager = user.id;
            this.selectedRelationshipManager = user; // Update the parent's selected user
        } else {
            this.client.relationshipManager = null; // Clear the user
            this.selectedRelationshipManager = null;
        }
    }
  }

  public updateInvestmentOfficer(user: UserItem): void {
    if(this.client) {
        if (user) {
            this.client.managedBy = user.id;
            this.selectedInvestmentOfficer = user; // Update the parent's selected user
        } else {
            this.client.managedBy = null; // Clear the user
            this.selectedInvestmentOfficer = null;
        }
    }
  }

  private assignUserManagementFields(): void {
    // Ensure user management fields are properly assigned before saving
    if (this.selectedAdmin) {
        this.client.administeredBy = this.selectedAdmin.id;
    }
    if (this.selectedRelationshipManager) {
        this.client.relationshipManager = this.selectedRelationshipManager.id;
    }
    if (this.selectedInvestmentOfficer) {
        this.client.managedBy = this.selectedInvestmentOfficer.id;
    }
  }

  public cancel() {
    this.$state.go("^");
  }

  // Helper methods for polymorphic type checking
  public isIndividualClient(): boolean {
    return this.clientDetailsService.isIndividual(this.client);
  }

  public isInstitutionClient(): boolean {
    return this.clientDetailsService.isInstitution(this.client);
  }

  public getIndividualClient(): IndividualClientDetails {
    return this.client as IndividualClientDetails;
  }

  public getInstitutionClient(): InstitutionClientDetails {
    return this.client as InstitutionClientDetails;
  }

  // Helper methods for client type icon display
  public getClientTypeIcon(): string {
    if (!this.client) {
      return 'person'; // Default icon
    }
    
    return this.client.clientType === 'Individual' ? 'person' : 'business';
  }

  public getClientTypeTooltip(): string {
    if (!this.client) {
      return 'Client Type'; // Default tooltip
    }
    
    return this.client.clientType === 'Individual' ? 'Individual' : 'Institution';
  }

  /**
   * Load all dropdown options once and store them for child components
   */
  private loadDropdownOptions(): void {
    this.isLoadingDropdownOptions = true;
    
    this.dropdownOptionsService.getAllOptions()
      .then((optionsCache) => {
        // Store all options in the controller for child components to access
        this.dropdownOptions = optionsCache;
        
        // Broadcast that options are loaded so child components can access them
        this.$rootScope.$broadcast('dropdownOptionsLoaded', this.dropdownOptions);
      })
      .catch((error) => {
        console.error('Failed to load dropdown options:', error);
        this.notifyjs("Failed to load dropdown options", "error");
      })
      .finally(() => {
        this.isLoadingDropdownOptions = false;
      });
  }

  /**
   * Get specific dropdown options by type
   */
  public getDropdownOptions(optionType: string): any[] {
    return this.dropdownOptions[optionType] || [];
  }

  /**
   * Refresh dropdown options (useful for admin functions)
   */
  public refreshDropdownOptions(): void {
    this.dropdownOptionsService.refreshOptions()
      .then((optionsCache) => {
        this.dropdownOptions = optionsCache;
        this.$rootScope.$broadcast('dropdownOptionsLoaded', this.dropdownOptions);
        this.notifyjs("Dropdown options refreshed", "success");
      })
      .catch((error) => {
        console.error('Failed to refresh dropdown options:', error);
        this.notifyjs("Failed to refresh dropdown options", "error");
      });
  }

  public isViewOnly(): boolean {
    // Check if user is view-only (either ViewOnly role or ClientDetailsViewOnly role)
    return this.authService.isViewOnly() || this.authService.isClientDetailsViewOnly();
  }
}

export const clientDetails: angular.IComponentOptions = {
  template: require("./clientdetails.html"),
  controller: ClientDetailsController,
  controllerAs: '$ctrl'
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/clientdetails.ts